import {Injectable} from '@angular/core';
import moment from 'moment';
import {Observable, of} from 'rxjs';
import {map} from 'rxjs/operators';
import {HttpClient, HttpParams} from '@angular/common/http';
import {CsvDownloadInfo} from '../classes/csv-download-info';
import {GenCounty} from '../../../../../generated/serverModels/GenCounty';
import {State} from '../classes/state';
import {GenAggregationLevel} from '../../../../../generated/serverModels/GenAggregationLevel';
import {ResponseWrapper} from '../../../classes/response-wrapper';
import {GenMax24HourCounty} from 'frontend/generated/serverModels/GenMax24HourCounty';
import {GenMax24HourState} from 'frontend/generated/serverModels/GenMax24HourState';
import {GenMax24HourZip} from 'frontend/generated/serverModels/GenMax24HourZip';
import {CountyCoverageData} from '../../../classes/county-coverage-data';
import {GenOutageSummaryTrend} from '../../../../../generated/serverModels/GenOutageSummaryTrend';
import {GenMaxImpact} from '../../../../../generated/serverModels/GenMaxImpact';
import {SummaryPeak} from '../classes/summary-peak';
import {GenOutageAggregationLevel} from '../../../../../generated/serverModels/GenOutageAggregationLevel';
import {GenOutageBreakdown} from '../../../../../generated/serverModels/GenOutageBreakdown';
import {OutageBreakdown} from '../classes/outage-breakdown';

@Injectable({
    providedIn: 'root',
})
export class OutageService {
    /** Cache of states' counties, for immediate response to repeat calls.  */
    protected countyCache: {[stateId: number]: GenCounty[]} = {};

    constructor(private http: HttpClient) {}

    public updateSummaryData(start: moment.Moment, end: moment.Moment): Observable<any> {
        const url = `/api/outagesummary/converttooutagesummary?start=${start.format()}&end=${end.format()}`;
        return this.http.get(url);
    }

    /**
     * Retrieves the counties for the specified States.
     * @note This method uses caching, returns cached values for any states previously requested
     */
    public getCountiesForStates(states: State[]): Observable<GenCounty[]> {
        let query: number[] = [];
        let cache: GenCounty[] = [];
        if (states.length) {
            const {cached, newQuery} = states.reduce(
                (p: {cached: number[]; newQuery: number[]}, s) =>
                    // Adds State IDs to cache group if counties available, else to query group
                    !this.countyCache[s.id] ? {...p, newQuery: [...p.newQuery, s.id]} : {...p, cached: [...p.cached, s.id]},
                {cached: [], newQuery: []}
            );
            // Merge all cached counties together, to reflect api response
            cache = cached.reduce((p: GenCounty[], c) => p.concat(this.countyCache[c]), []);
            query = newQuery;
        }

        if (!query.length && !!states.length) {
            // If no states to be queried BUT specific states were requested, return them from cache
            return of(cache);
        }

        const url = `api/location/county?format=None&stateId=${query.join('&stateId=')}`;
        return this.http.get<GenCounty[]>(url).pipe(
            /** Add any cached state counties to the response & add the response to the cache. */
            map((counties: GenCounty[]) => {
                const newCache = {};
                counties.forEach((county) => {
                    const newState = newCache[county.stateId] ?? [];
                    newCache[county.stateId] = newState.concat(county);
                });
                this.countyCache = {...this.countyCache, ...newCache};
                return counties.concat(cache);
            })
        );
    }

    public sendCsvDownloadEmail(info: CsvDownloadInfo): Observable<any> {
        const url = 'api/outagesummary/csvdownload';
        return this.http.post(url, info);
    }

    public getPaginatedOutageData<T extends GenMax24HourCounty | GenMax24HourState | GenMax24HourZip>(
        aggregationLevel: GenAggregationLevel,
        runStartTime: moment.Moment,
        states?: State[],
        page: number = 1,
        itemsPerPage: number = 25,
        sort?: string
    ): Observable<ResponseWrapper<T>> {
        let endpoint: string;
        let params = new HttpParams().set('start', runStartTime.format());

        params = params.set('page', page);
        params = params.set('pageSize', itemsPerPage);
        params = params.set('sort', sort);

        if (states?.length !== 0) {
            params = params.append('state', states.map((state) => state.abbreviation).join());
        }

        switch (aggregationLevel.toString()) {
            case GenAggregationLevel.state.toString():
                endpoint = `statemax24hoursummary`;
                break;
            case GenAggregationLevel.county.toString():
                endpoint = `countymax24hoursummary`;
                params = params.set(`allCounties`, true);
                break;
            case GenAggregationLevel.zip.toString():
                endpoint = `zipmax24hoursummary`;
                break;
            default:
                console.warn('Trying to get paginated data for unknown aggregation level: ' + aggregationLevel.toString());
        }

        return this.http.get<ResponseWrapper>(`api/outagesummary/${endpoint}-paginated`, {params});
    }

    public getCsvDownloadRowCount(aggregationLevel: GenAggregationLevel, fipsCodes: string[]): Observable<number> {
        const url = 'api/outagesummary/csvdownload-row-count';
        const params = new HttpParams().set('aggregation', aggregationLevel.name).set('fips', fipsCodes.join(','));
        return this.http.get<number>(url, {params});
    }

    public getUtilitiesByCounty(fipsCode: string, runStartTime?: moment.Moment): Observable<CountyCoverageData[]> {
        const url = `api/coverage/county/${fipsCode}`;
        let params = new HttpParams();
        if (runStartTime) {
            params = params.set('timestamp', runStartTime.format());
        }

        return this.http
            .get<ResponseWrapper>(url, {params})
            .pipe(map((res: ResponseWrapper) => res.data.map((coverage) => new CountyCoverageData(coverage))));
    }

    public getOutageSummaryTrend(
        level: 'county' | 'state' | 'fema' | 'national' | 'utility',
        end: moment.Moment,
        start?: moment.Moment,
        states?: State[],
        showZeroOutages: boolean = false
    ): Observable<GenOutageSummaryTrend[]> {
        const url = `api/outagesummary/outagesummarytrend`;
        const stateIds = states?.map((state) => state.id) || [];

        // Setting the start date here to match what the middle tier would do, this will allow us to better debug any frontend issues since we know the exact dates being passed in.
        if (!start) {
            start = end.clone().subtract(15, 'minutes');
        }

        const params = new HttpParams()
            .set('level', level)
            .set('end', end.format())
            .set('start', start.format())
            .set('showZero', showZeroOutages);

        return this.http.get(url, {params}).pipe(
            map((res: ResponseWrapper) => {
                return (
                    res.data
                        .map((trend) => new GenOutageSummaryTrend(trend))
                        // We should move this filter call to the backend
                        .filter((trend) => stateIds.length === 0 || stateIds.includes(trend.stateId))
                );
            })
        );
    }

    public getImpactMetric(eventId: number, runStartDate: moment.Moment, states: State[], start?: moment.Moment): Observable<GenMaxImpact> {
        const url = `api/event/impact`;
        let params = new HttpParams()
            .set('eventId', eventId)
            .set('end', runStartDate.format())
            .set('state', states.map((state) => state.abbreviation).join());

        if (start) {
            params = params.set('start', start.format());
        }

        return this.http.get<any>(url, {params}).pipe(map((res) => new GenMaxImpact(res)));
    }

    public getSummaryWithPeak(
        eventId: number,
        groupBy: 'state' | 'county',
        startDate?: moment.Moment,
        endDate?: moment.Moment
    ): Observable<SummaryPeak[]> {
        const url = `api/outagesummary/summary-with-peak`;
        let params = new HttpParams().set('eventId', eventId).set('groupBy', groupBy);

        if (startDate) {
            params = params.append('startTime', startDate.format());
        }

        if (endDate) {
            params = params.append('currentTime', endDate.format());
        }

        return this.http.get(url, {params}).pipe(map((res: ResponseWrapper) => res.data.map((d) => new SummaryPeak(d))));
    }

    getOutageBreakdowns(value: GenOutageAggregationLevel, runStartTime: moment.Moment, states: State[]): Observable<OutageBreakdown[]> {
        const url = `api/outagesummary/outage-breakdown`;
        let groupBy = 'county';
        switch (value) {
            case GenOutageAggregationLevel.state:
                groupBy = 'state';
                break;
            case GenOutageAggregationLevel.county:
                groupBy = 'county';
                break;
            case GenOutageAggregationLevel.utility:
                groupBy = 'countyUtility';
                break;
        }

        const params = new HttpParams().set('groupBy', groupBy).set('runStartTime', runStartTime.format());

        const stateIds = states.map((state) => state.id);

        return this.http.get(url, {params}).pipe(
            map((res: ResponseWrapper) => {
                return res.data.map((d) => new OutageBreakdown(d)).filter((d) => stateIds.includes(d.stateId));
            })
        );
    }
}
